import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Target, Plus, Star, Trash2, Sparkles, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { completeMission } from '../../utils/missionHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dream categories for middle schoolers
const dreamCategories = [
  { id: 'career', emoji: '🚀', label: 'Dream Job', color: 'from-blue-500 to-purple-500' },
  { id: 'adventure', emoji: '✈️', label: 'Places to Visit', color: 'from-green-500 to-teal-500' },
  { id: 'skills', emoji: '🎯', label: 'Skills to Learn', color: 'from-orange-500 to-red-500' },
  { id: 'fun', emoji: '🎮', label: 'Fun Goals', color: 'from-pink-500 to-purple-500' }
];

export default function MSDreamsPage() {
  const { token } = useAuth();
  const [dreams, setDreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDream, setNewDream] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('career');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchDreams();
  }, []);

  const fetchDreams = async () => {
    try {
      const response = await axios.get(`${API}/goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both array and object response formats
      const data = response.data;
      if (Array.isArray(data)) {
        setDreams(data);
      } else if (data?.goals && Array.isArray(data.goals)) {
        setDreams(data.goals);
      } else {
        setDreams([]);
      }
    } catch (error) {
      console.error('Failed to fetch dreams');
      setDreams([]);
    } finally {
      setLoading(false);
    }
  };

  const addDream = async () => {
    if (!newDream.trim()) return;
    
    setAdding(true);
    try {
      const response = await axios.post(`${API}/goals`, {
        title: newDream,
        category: selectedCategory,
        description: '',
        target_date: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDreams(prev => [response.data, ...prev]);
      setNewDream('');
      toast.success('Dream added!');
      completeMission(token, 'dream');
    } catch (error) {
      toast.error('Failed to add dream');
    } finally {
      setAdding(false);
    }
  };

  const toggleComplete = async (dream) => {
    try {
      await axios.put(`${API}/goals/${dream.id}`, {
        completed: !dream.completed
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDreams(prev => prev.map(d => 
        d.id === dream.id ? { ...d, completed: !d.completed } : d
      ));
      
      if (!dream.completed) {
        toast.success('Amazing! Dream achieved! 🎉');
      }
    } catch (error) {
      console.error('Failed to update');
    }
  };

  const deleteDream = async (dreamId) => {
    try {
      await axios.delete(`${API}/goals/${dreamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDreams(prev => prev.filter(d => d.id !== dreamId));
      toast.success('Dream removed');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const groupedDreams = dreamCategories.map(cat => ({
    ...cat,
    dreams: dreams.filter(d => d.category === cat.id)
  }));

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">My Dreams</h1>
            <p className="text-slate-300">Dream big - no limits here!</p>
          </div>
        </div>
      </motion.div>

      {/* Add New Dream */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-8"
      >
        <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          Add a New Dream
        </h3>
        
        {/* Category Selection */}
        <div className="flex flex-wrap gap-2 mb-4">
          {dreamCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${
                selectedCategory === cat.id
                  ? `bg-gradient-to-r ${cat.color} text-white shadow-lg`
                  : 'bg-gradient-to-r from-slate-700 to-slate-600 text-white hover:from-slate-600 hover:to-slate-500'
              }`}
            >
              <span>{cat.emoji}</span>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Input */}
        <div className="flex gap-3">
          <Input
            placeholder="What's your dream? (e.g., Visit Japan, Learn guitar)"
            value={newDream}
            onChange={(e) => setNewDream(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addDream()}
            className="input-dark flex-1"
            data-testid="new-dream-input"
          />
          <Button
            onClick={addDream}
            disabled={adding || !newDream.trim()}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 font-semibold"
          >
            <Plus className="w-5 h-5 mr-1" />
            Add Dream!
          </Button>
        </div>
      </motion.div>

      {/* Dreams by Category */}
      {loading ? (
        <div className="text-center text-slate-300 py-12">Loading your dreams...</div>
      ) : (
        <div className="space-y-6">
          {groupedDreams.map((category, catIndex) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: catIndex * 0.1 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">{category.emoji}</span>
                <h3 className="text-lg font-semibold text-white">{category.label}</h3>
                <span className="text-slate-300 text-sm">({category.dreams.length})</span>
              </div>

              {category.dreams.length === 0 ? (
                <div className="glass-card p-4 text-center text-slate-300">
                  No dreams here yet. Add one above! ✨
                </div>
              ) : (
                <div className="space-y-2">
                  <AnimatePresence>
                    {category.dreams.map((dream) => (
                      <motion.div
                        key={dream.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className={`glass-card p-4 flex items-center gap-3 ${
                          dream.completed ? 'bg-green-500/10 border-green-500/30' : ''
                        }`}
                      >
                        <button
                          onClick={() => toggleComplete(dream)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            dream.completed
                              ? 'bg-green-500 text-white'
                              : 'border-2 border-slate-600 hover:border-green-500'
                          }`}
                        >
                          {dream.completed && <CheckCircle className="w-5 h-5" />}
                        </button>
                        
                        <span className={`flex-1 ${
                          dream.completed ? 'text-green-300 line-through' : 'text-white'
                        }`}>
                          {dream.title}
                        </span>

                        <button
                          onClick={() => deleteDream(dream.id)}
                          className="p-2 text-slate-300 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Encouragement */}
      {dreams.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-8 text-center text-slate-300"
        >
          <p>🌟 You have {dreams.length} dream{dreams.length !== 1 ? 's' : ''}! Keep dreaming big!</p>
        </motion.div>
      )}
    </div>
  );
}
